/**
 * Showcase Slider — canvas preview of the frontend composition.
 *
 * The stacked authoring view beside this one shows every slide whole, which is
 * what an author editing slide three needs. What it cannot show is the thing
 * the block exists for: the panel holds its place while its content slides
 * through it, and the photo at the edge becomes the next hero shot. That is a
 * motion between slides, so no still frame of any single slide contains it.
 *
 * This is that motion, rebuilt in the canvas. The markup mirrors render.php
 * region for region — media strip, panel track, edge preview — so style.scss
 * dresses it without a single editor-only rule for the composition itself; the
 * editor stylesheet only has to supply the movement Embla would have supplied.
 *
 * Movement is a percentage transform per track, never a measured pixel offset.
 * Each track is a flex row as wide as its own viewport whose items are each
 * `flex: 0 0 100%`, so one item is exactly 100% of the track — which makes
 * `translateX(-index × 100%)` per track reproduce the frontend's parallax for
 * free, because the panel's 100% and the media's 100% are different distances.
 * That is the same consequence-of-layout the frontend relies on, arrived at
 * without measuring anything, so it cannot drift out of step on resize.
 *
 * @package BrandyBlocks
 */

import { __experimentalUseBlockPreview as useBlockPreview } from '@wordpress/block-editor';
import { __, sprintf } from '@wordpress/i18n';

import { resolvePresetValue } from '../../../utils/helpers';
import { controlColorVars } from '../../../utils/slider-control-colors';

const ARROWS = {
	prev: (
		<svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
			<path d="M6 11L1 6L6 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	),
	next: (
		<svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
			<path d="M1 1L6 6L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	),
};

/**
 * One authored half, rendered read-only.
 *
 * A component rather than a call in a loop because useBlockPreview is a hook —
 * one instance per half is what lets there be N of them.
 *
 * @param {Object}   props
 * @param {Object[]} props.blocks    The half's block subtree.
 * @param {string}   props.className Wrapper class from render.php.
 * @return {Element} Read-only preview of the half.
 */
function HalfPreview({ blocks, className }) {
	const previewProps = useBlockPreview({ blocks, props: { className } });

	return <div {...previewProps} />;
}

/**
 * The frontend composition, parked on one slide.
 *
 * @param {Object}   props
 * @param {Object}   props.blockProps   Wrapper props from useBlockProps.
 * @param {Object[]} props.slides       Slide blocks, each holding two halves.
 * @param {Object}   props.attributes   The block's own attributes.
 * @param {number}   props.index        Active slide.
 * @param {Function} props.onSelect     Jump to a slide.
 * @return {Element} Canvas preview.
 */
export default function PreviewComposition({ blockProps, slides, attributes, index, onSelect }) {
	const {
		layout = 'editorial',
		panelSide = 'right',
		panelWidth = 42,
		mediaHeight = '540px',
		showPreview = true,
		previewPeek = 8,
		showArrows = true,
		showCounter = true,
	} = attributes;

	/*
	 * A slide short of its two halves is skipped rather than rendered partially,
	 * exactly as render.php does — a partial slide would shift every panel after
	 * it out of step with its photo, which reads as a bug in the block.
	 */
	const pairs = slides
		.map((slide) => slide.innerBlocks || [])
		.filter((halves) => halves.length >= 2)
		.map((halves) => ({ media: halves[0], panel: halves[1] }));

	const count = pairs.length;
	const active = Math.min(index, Math.max(0, count - 1));

	// Both suppressions mirror render.php: the edge preview only means something
	// beside a panel that leaves room for it, and one slide has nothing to move.
	const hasPreview = showPreview && layout === 'editorial' && count > 1;
	const hasArrows = showArrows && count > 1;
	const hasCounter = showCounter && count > 1;

	const panelColor = slides[active]?.attributes?.panelColor || '';

	// One slide ahead, wrapping — advancing promotes the photo the reader was
	// already peeking at, which is what makes the strip read as continuous.
	const previewPairs = hasPreview ? [...pairs.slice(1), ...pairs.slice(0, 1)] : [];

	return (
		<div
			{...blockProps}
			data-panel-side={panelSide}
			data-nav-style="default"
			data-dots-style="fraction"
			style={{
				...blockProps.style,
				'--showcase-panel-width': `${panelWidth}%`,
				'--showcase-preview-peek': `${hasPreview ? previewPeek : 0}%`,
				'--showcase-media-height': mediaHeight || '540px',
				// Read by editor.scss to move every track by its own item width.
				'--showcase-preview-index': active,
				// render.php appends these to this same node, which is the
				// `.embla` element there — the one embla.min.css seeds the control
				// colours on, so the override has to sit on it. The dots half feeds
				// the counter, which is this block's pinned `fraction` style.
				...controlColorVars(attributes),
			}}
		>
			<div className="showcase__media">
				<div className="embla__viewport">
					<div className="embla__container">
						{pairs.map((pair, slide) => (
							<div className="embla__slide showcase__media-item" key={pair.media.clientId || slide}>
								<HalfPreview blocks={[pair.media]} />
							</div>
						))}
					</div>
				</div>
			</div>

			<div
				className="showcase__panel"
				// The frontend cross-fades this from data-panel-colors; here the
				// active slide's colour is known outright, so it is set directly.
				style={
					panelColor
						? { backgroundColor: resolvePresetValue(panelColor) }
						: undefined
				}
			>
				<div className="showcase__panel-viewport">
					<div className="showcase__track">
						{pairs.map((pair, slide) => (
							<div className="showcase__track-item" key={pair.panel.clientId || slide}>
								<HalfPreview blocks={[pair.panel]} />
							</div>
						))}
					</div>
				</div>

				{(hasArrows || hasCounter) && (
					<div className="showcase__chrome embla__controls">
						{hasArrows && (
							<button
								type="button"
								className="embla__button embla__button--prev"
								onClick={() => onSelect(active - 1)}
								disabled={active === 0}
								aria-label={__('Previous slide', 'brandy-blocks')}
							>
								{ARROWS.prev}
							</button>
						)}
						{hasCounter && (
							<div className="embla__dots embla__dots--fraction">
								{sprintf(
									/* translators: 1: current slide number, 2: total slides. */
									__('%1$d / %2$d', 'brandy-blocks'),
									active + 1,
									count
								)}
							</div>
						)}
						{hasArrows && (
							<button
								type="button"
								className="embla__button embla__button--next"
								onClick={() => onSelect(active + 1)}
								disabled={active >= count - 1}
								aria-label={__('Next slide', 'brandy-blocks')}
							>
								{ARROWS.next}
							</button>
						)}
					</div>
				)}
			</div>

			{hasPreview && (
				<div className="showcase__preview" aria-hidden="true">
					<div className="showcase__track">
						{previewPairs.map((pair, slide) => (
							<div className="showcase__track-item" key={pair.media.clientId || slide}>
								<HalfPreview blocks={[pair.media]} />
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
